// ACCOUNT SUBSCRIPTION — CANCELLATION ************************************************************
// pathname: /v1/account-subscriptions/{id}/close-request
// methods:  POST (request), DELETE (withdraw)
// ************************************************************************************************
import { useSwrMutationApi } from '@hooks/useSwrApi/useSwrMutationApi';
import type { ApiSchema } from '@type/api/apiUtilityFn';

type AccountSubscription = ApiSchema<'AccountSubscriptionRead'>;

interface UseSwrSubscriptionCloseRequestProps {
  subscriptionId: number;
  locale?: string;
  onSuccess?: (subscription: AccountSubscription) => void;
  successToastText?: string;
}

/**
 * @description SWR mutation: ask for the subscription not to renew.
 *
 * It keeps working until the end of the period already paid for and closes on the
 * renewal date — so the confirmation copy should say "until <date>", not "cancelled".
 */
export const useSwrRequestSubscriptionClose = (props: UseSwrSubscriptionCloseRequestProps) => {
  const {
    subscriptionId, locale, onSuccess, successToastText,
  } = props;

  const {
    data, error, isMutating, reset, trigger,
  } = useSwrMutationApi<AccountSubscription, undefined>({
    url: `/v1/account-subscriptions/${subscriptionId}/close-request`,
    locale,
    fetcherConfig: {
      method: 'POST',
    },
    apiOptions: {
      onSuccess: (response) => { if (onSuccess) onSuccess(response); },
    },
    toastsOptions: {
      showSuccessToast: true,
      successToastText,
      showErrorToast: true,
    },
  });

  return {
    data, error, isMutating, reset, trigger,
  };
};

/**
 * @description SWR mutation: withdraw a cancellation, so the subscription renews as normal.
 *
 * Refused with 409 when the cancellation was raised because the account is
 * suspended — that one is cleared by settling the balance, not by asking. The
 * page should only offer this action when `closeRequestRevocable` is true, which
 * is exactly what that field is for.
 */
export const useSwrRevokeSubscriptionClose = (props: UseSwrSubscriptionCloseRequestProps) => {
  const {
    subscriptionId, locale, onSuccess, successToastText,
  } = props;

  const {
    data, error, isMutating, reset, trigger,
  } = useSwrMutationApi<AccountSubscription, undefined>({
    url: `/v1/account-subscriptions/${subscriptionId}/close-request`,
    locale,
    fetcherConfig: {
      method: 'DELETE',
    },
    apiOptions: {
      onSuccess: (response) => { if (onSuccess) onSuccess(response); },
    },
    toastsOptions: {
      showSuccessToast: true,
      successToastText,
      showErrorToast: true,
    },
  });

  return {
    data, error, isMutating, reset, trigger,
  };
};
